import React, { useState } from "react"
import { DebugData, CachedFieldDebug } from "~/api/quarterly_reports"

interface DebugModalProps {
  debugData: DebugData
  onClose: () => void
}

const DebugModal: React.FC<DebugModalProps> = ({ debugData, onClose }) => {
  const [selectedField, setSelectedField] = useState<string | null>(null)
  const [selectedView, setSelectedView] = useState<
    "output" | "metadata" | "raw"
  >("output")

  const aiFields = [
    { key: "attribution_highlights", label: "Attribution Highlights" },
    { key: "liquidity_events", label: "Liquidity Events" },
    { key: "new_investments", label: "New Investments" },
    { key: "cash_flows", label: "Cash Flows" },
    { key: "high_level_summary", label: "High Level Summary" },
  ]

  const dataFields = [
    { key: "performance_by_series", label: "Performance by Series" },
    { key: "performance_by_stage", label: "Performance by Stage" },
    { key: "performance_by_asset_class", label: "Performance by Asset Class" },
    {
      key: "top_ten_portfolio_companies",
      label: "Top Ten Portfolio Companies",
    },
    { key: "geographic_exposure", label: "Geographic Exposure" },
    { key: "sector_exposure", label: "Sector Exposure" },
  ]

  const getFieldData = (
    key: string,
  ): CachedFieldDebug | { output: any; raw: any } | undefined => {
    return debugData.cached_data[key as keyof typeof debugData.cached_data]
  }

  const selectedFieldData = selectedField ? getFieldData(selectedField) : null

  const hasMetadata = (data: any): data is CachedFieldDebug => {
    return data && "metadata" in data
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="debug-modal" onClick={(e) => e.stopPropagation()}>
        <div className="debug-modal-header">
          <h2>Debug: Cached Data & AI Metadata</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="debug-modal-body">
          <div className="debug-sidebar">
            <h3>AI Generated Fields</h3>
            <ul className="debug-field-list">
              {aiFields.map(({ key, label }) => (
                <li
                  key={key}
                  className={selectedField === key ? "selected" : ""}
                  onClick={() => setSelectedField(key)}
                >
                  {label}
                </li>
              ))}
            </ul>

            <h3>Data Fields</h3>
            <ul className="debug-field-list">
              {dataFields.map(({ key, label }) => (
                <li
                  key={key}
                  className={selectedField === key ? "selected" : ""}
                  onClick={() => setSelectedField(key)}
                >
                  {label}
                </li>
              ))}
            </ul>
          </div>

          <div className="debug-content">
            {!selectedField ? (
              <div className="debug-placeholder">
                <p>Select a field from the sidebar to view its cached data</p>
              </div>
            ) : (
              <>
                <div className="debug-tabs">
                  <button
                    className={selectedView === "output" ? "active" : ""}
                    onClick={() => setSelectedView("output")}
                  >
                    Output
                  </button>
                  {hasMetadata(selectedFieldData) && (
                    <button
                      className={selectedView === "metadata" ? "active" : ""}
                      onClick={() => setSelectedView("metadata")}
                    >
                      Metadata
                    </button>
                  )}
                  <button
                    className={selectedView === "raw" ? "active" : ""}
                    onClick={() => setSelectedView("raw")}
                  >
                    Raw JSON
                  </button>
                </div>

                <div className="debug-data-view">
                  {selectedView === "output" && (
                    <pre>
                      {JSON.stringify(selectedFieldData?.output, null, 2)}
                    </pre>
                  )}
                  {selectedView === "metadata" &&
                    hasMetadata(selectedFieldData) && (
                      <div className="debug-metadata">
                        {selectedFieldData.metadata ? (
                          <>
                            <div className="metadata-section">
                              <h4>Generation Info</h4>
                              <p>
                                <strong>Service:</strong>{" "}
                                {selectedFieldData.metadata.service || "N/A"}
                              </p>
                              <p>
                                <strong>Model:</strong>{" "}
                                {selectedFieldData.metadata.model || "N/A"}
                              </p>
                              <p>
                                <strong>Generated At:</strong>{" "}
                                {selectedFieldData.metadata.generated_at ||
                                  "N/A"}
                              </p>
                            </div>

                            {selectedFieldData.metadata.prompts && (
                              <div className="metadata-section">
                                <h4>System Prompt</h4>
                                <pre className="prompt-text">
                                  {selectedFieldData.metadata.prompts.system ||
                                    "N/A"}
                                </pre>

                                <h4>User Prompt</h4>
                                <pre className="prompt-text">
                                  {selectedFieldData.metadata.prompts.user ||
                                    "N/A"}
                                </pre>
                              </div>
                            )}

                            {selectedFieldData.metadata.context && (
                              <div className="metadata-section">
                                <h4>Context</h4>
                                <pre>
                                  {JSON.stringify(
                                    selectedFieldData.metadata.context,
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            )}

                            {selectedFieldData.metadata.input_data && (
                              <div className="metadata-section">
                                <h4>Input Data</h4>
                                <pre>
                                  {JSON.stringify(
                                    selectedFieldData.metadata.input_data,
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            )}
                          </>
                        ) : (
                          <p>No metadata available (old format)</p>
                        )}
                      </div>
                    )}
                  {selectedView === "raw" && (
                    <pre>{JSON.stringify(selectedFieldData?.raw, null, 2)}</pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DebugModal
